import { SnackbarProvider, closeSnackbar, useSnackbar } from "notistack";
import * as React from "react";
import { createContext, ReactNode, useCallback, useContext, useMemo } from "react";
import { bindConfirmationDialog, ConfirmationDialog, ConfirmationDialogInitValues, ConfirmationDialogResultReason, useConfirmationDialogState } from "../../components/confirmation-dialog/ConfirmationDialog";
import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";

export type AcknowledgeVariantType = "default" | "error" | "success" | "warning" | "info";

export type ConfirmationContextProviderProps = {
    children: ReactNode;
};

export type ProviderContext = {
    confirm: (options: ConfirmationDialogInitValues) => Promise<ConfirmationDialogResultReason>;
    acknowledge: (message: string | React.ReactNode, variant?: AcknowledgeVariantType) => string | number;
};

const ConfirmationContext = createContext<ProviderContext | undefined>(undefined);

export function ConfirmationContextProvider({ children }: ConfirmationContextProviderProps) {
    return (
        <SnackbarProvider
            anchorOrigin={{
                vertical: "top",
                horizontal: "right"
            }}
            action={(snackbarId) => (
                <IconButton onClick={() => closeSnackbar(snackbarId)} color="inherit">
                    <Close />
                </IconButton>
            )}
        >
            <ConfirmationContextProviderBase>
                {children}
            </ConfirmationContextProviderBase>
        </SnackbarProvider>
    );
}

function ConfirmationContextProviderBase({ children }: ConfirmationContextProviderProps) {

    const confirmationDialog = useConfirmationDialogState();
    const { enqueueSnackbar } = useSnackbar();

    const confirm = useCallback(async (options: ConfirmationDialogInitValues) => {
        return (await confirmationDialog.open(options)).reason;
    }, [confirmationDialog]);

    const acknowledge = useCallback((message: string | React.ReactNode, variant?: AcknowledgeVariantType) => {
        return enqueueSnackbar(message, { variant: variant});
    }, [enqueueSnackbar]);

    const context: ProviderContext = useMemo(() => {
        return {
            confirm,
            acknowledge
        };
    }, [confirm, acknowledge]);

    return (
        <ConfirmationContext.Provider value={context}>
            {children}
            <ConfirmationDialog {...bindConfirmationDialog(confirmationDialog)} />
        </ConfirmationContext.Provider>
    );
}

export function useConfirmation(): ProviderContext {
    const context = useContext<ProviderContext | undefined>(ConfirmationContext);
    if (!context) {
        throw new Error("useConfirmationContext must be used within a ConfirmationContextProvider");
    }
    return context;
}
